import { useEffect, useRef, useState } from "react"
import { get } from "helpers/api_helper"

// One entry-context read serves the admission buttons and "Operations in progress".
export default function useEntryAvailability({ eventId, competitionId, refreshKey, isOpen }) {
  const [loaded, setLoaded] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const reload = useRef(null)
  useEffect(() => {
    let active = true, loading = false
    setLoaded(null)
    const load = async () => {
      if (loading) return
      loading = true
      try {
        const response = await get(`/admin/events/${eventId}/competitions/${competitionId}/entry-context`, { timeout: 15000 })
        const permissions = response?.data?.permissions
        if (["addEntry", "addWildCard"].some(key => typeof permissions?.[key]?.allowed !== "boolean")) throw new Error("Unavailable")
        const operations = Array.isArray(response?.data?.operations) ? response.data.operations : null
        if (active) setLoaded({ eventId, competitionId, permissions, operations })
      } catch { if (active) setLoaded({ eventId, competitionId, error: true, operations: null }) }
      finally { loading = false }
    }
    reload.current = load
    load()
    const timer = setInterval(load, 30000)
    window.addEventListener("focus", load)
    return () => { active = false; reload.current = null; clearInterval(timer); window.removeEventListener("focus", load) }
  }, [eventId, competitionId, refreshKey, isOpen, attempt])
  const current = loaded?.eventId === eventId && loaded?.competitionId === competitionId ? loaded : null
  return { error: current?.error, retry: () => setAttempt(value => value + 1),
    // Reads again without clearing what is shown, after a Retry or Close.
    refresh: () => reload.current?.(),
    // undefined while loading, null when unavailable, otherwise the open operations.
    operations: current ? current.operations : undefined,
    reason: key => current?.error ? "Entry availability could not be checked. Retry below."
      : !current ? "Checking entry availability…" : current.permissions[key].allowed ? ""
        : current.permissions[key].reason || "This entry action is currently unavailable." }
}
